import React, { useState, useEffect } from 'react';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';
import { API_CONFIG } from '../config';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import ConfirmationModal from './ui/ConfirmationModal';

interface User {
  id: string;
  username: string;
  email: string;
  role: 'Super Admin' | 'Admin' | 'Producer' | 'Project Manager' | 'Coordinator' | 'Sales' | /* 'Client' | */ 'Demo';  // Client закомментирован
  createdAt: string;
}

interface UserManagementTabProps {
  onDataRefresh?: () => void;
  currentRole?: string | null;
}

// Helper function to convert role names to camelCase keys for localization
const getRoleKey = (role: string): string => {
  const roleMap: Record<string, string> = {
    'Super Admin': 'superAdmin',
    'Admin': 'admin',
    'Producer': 'producer',
    'Project Manager': 'projectManager',
    'Coordinator': 'coordinator',
    'Sales': 'sales',
    // 'Client': 'client',  // Закомментировано - используем Demo как Client
    'Demo': 'demo'  // Demo отображается как "Клиент" через локализацию
  };
  return roleMap[role] || 'producer';
};

const UserManagementTab: React.FC<UserManagementTabProps> = ({ onDataRefresh, currentRole }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeSubTab, setActiveSubTab] = useState<'create' | 'manage'>('create');
  
  // New user form state
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'Producer' as User['role']
  });
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [passwordMode, setPasswordMode] = useState<'manual' | 'generate'>('manual');

  // Edit user state
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    role: 'Producer' as User['role']
  });
  
  // Delete user confirmation modal
  const [deleteUserModal, setDeleteUserModal] = useState<{ isOpen: boolean; userId: string; username: string }>({ isOpen: false, userId: '', username: '' });

  // Generate random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setGeneratedPassword(password);
    setNewUser({ ...newUser, password });
    return password;
  };

  // Copy password to clipboard
  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  // Load users
  const loadUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/auth/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load users');
      }

      const data = await response.json();
      // Filter out telegram_bot from the list (additional safety check)
      // Check both username and email fields
      setUsers((data || []).filter((user: any) => {
        const username = (user.username || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        return username !== 'telegram_bot' && !username.includes('telegram_bot') && !email.includes('telegram_bot');
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Create user
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate username
    if (!newUser.username || !newUser.username.trim()) {
      setError('Username is required');
      setLoading(false);
      return;
    }

    // Validate email
    if (!newUser.email || !newUser.email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Validate password for manual mode
    if (passwordMode === 'manual' && (!newUser.password || !newUser.password.trim())) {
      setError('Password is required when using manual mode');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('spellbook_token');
      const requestBody: any = {
        username: newUser.username,
        email: newUser.email,
        role: newUser.role
      };
      
      // Only include password if manual mode is selected
      if (passwordMode === 'manual') {
        requestBody.password = newUser.password;
      }

      const response = await fetch(`${API_CONFIG.API_URL}/auth/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Show validation details if available
        if (errorData.details && Array.isArray(errorData.details) && errorData.details.length > 0) {
          const detailsMessages = errorData.details.map((d: any) => d.message).join(', ');
          throw new Error(`${errorData.error || 'Validation error'}: ${detailsMessages}`);
        }
        throw new Error(errorData.error || 'Failed to create user');
      }

      // Reset form
      setNewUser({
        username: '',
        email: '',
        password: '',
        role: 'Producer'
      });
      setGeneratedPassword('');
      setPasswordMode('manual');

      // Reload users
      await loadUsers();
      onDataRefresh?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  // Delete user - open confirmation modal
  const handleDeleteUser = (userId: string, username: string) => {
    setDeleteUserModal({ isOpen: true, userId, username });
  };
  
  // Confirm delete user
  const confirmDeleteUser = async () => {
    const { userId } = deleteUserModal;
    setDeleteUserModal({ isOpen: false, userId: '', username: '' });

    try {
      setLoading(true);
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/auth/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      await loadUsers();
      onDataRefresh?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  // Start editing user
  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditForm({
      username: user.username,
      email: user.email,
      role: user.role
    });
  };

  // Update user
  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/auth/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user');
      }

      setEditingUser(null);
      await loadUsers();
      onDataRefresh?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap: { [key: string]: string } = {
      'Super Admin': t('userManagement.roles.superAdmin'),
      'Admin': t('userManagement.roles.admin'),
      'Producer': t('userManagement.roles.producer'),
      'Project Manager': t('userManagement.roles.projectManager'),
      'Coordinator': t('userManagement.roles.coordinator'),
      'Sales': t('userManagement.roles.sales'),
      'Demo': t('userManagement.roles.demo')
    };
    return roleMap[role] || role;
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex space-x-1 bg-gray-700 rounded-lg p-1">
        <button
          onClick={() => setActiveSubTab('create')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeSubTab === 'create'
              ? 'bg-indigo-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-600'
          }`}
        >
          <ICONS.Plus className="w-4 h-4 inline mr-2" />
          {t('system.users.create')}
        </button>
        <button
          onClick={() => setActiveSubTab('manage')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeSubTab === 'manage'
              ? 'bg-indigo-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-600'
          }`}
        >
          <ICONS.Users className="w-4 h-4 inline mr-2" />
          {t('system.users.manage')}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-900/20 border border-red-600 rounded-md p-4">
          <div className="flex">
            <ICONS.AlertCircle className="w-5 h-5 text-red-400 mr-2 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-400">Error</h3>
              <p className="text-sm text-red-300 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Create User Tab */}
      {activeSubTab === 'create' && (
        <div className="bg-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <ICONS.Plus className="w-5 h-5 mr-2" />
            {t('system.users.createNew')}
          </h3>
          
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('userManagement.username')}
                id="username"
                name="username"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="Введите имя пользователя"
                required
              />
              <Input
                label={t('userManagement.email')}
                id="email"
                name="email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="Введите email адрес"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('userManagement.password')}
              </label>
              
              {/* Password mode selector */}
              <div className="flex gap-4 mb-3">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="passwordMode"
                    value="manual"
                    checked={passwordMode === 'manual'}
                    onChange={(e) => {
                      setPasswordMode('manual');
                      setNewUser({ ...newUser, password: '' });
                      setGeneratedPassword('');
                    }}
                    className="mr-2 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-gray-300">Ввести пароль вручную</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="passwordMode"
                    value="generate"
                    checked={passwordMode === 'generate'}
                    onChange={(e) => {
                      setPasswordMode('generate');
                      setNewUser({ ...newUser, password: '' });
                      setGeneratedPassword('');
                    }}
                    className="mr-2 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-gray-300">Сгенерировать временный пароль и отправить по email</span>
                </label>
              </div>

              {/* Manual password input */}
              {passwordMode === 'manual' && (
                <>
              <Input
                    label=""
                id="password"
                name="password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Введите пароль"
                required
              />
              <div className="flex space-x-2 mt-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={generatePassword}
                  className="px-3"
                >
                  <ICONS.Refresh className="w-4 h-4" />
                </Button>
                {generatedPassword && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={copyPassword}
                    className="px-3"
                  >
                    <ICONS.Copy className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {generatedPassword && (
                <p className="text-xs text-gray-400 mt-1">
                  {t('userManagement.hintPassword')}: {generatedPassword}
                </p>
                  )}
                </>
              )}

              {/* Generate mode info */}
              {passwordMode === 'generate' && (
                <div className="bg-blue-900/20 border border-blue-700 rounded-md p-3">
                  <p className="text-sm text-blue-300">
                    📧 Временный пароль будет автоматически сгенерирован и отправлен на указанный email адрес. 
                    Пользователю потребуется сменить пароль при первом входе в систему.
                  </p>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <Select
                label={t('userManagement.role')}
                id="role"
                name="role"
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value as User['role'] })}
                required
              >
                <option value="Super Admin">{t('userManagement.roles.superAdmin')}</option>
                <option value="Admin">{t('userManagement.roles.admin')}</option>
                <option value="Producer">{t('userManagement.roles.producer')}</option>
                <option value="Project Manager">{t('userManagement.roles.projectManager')}</option>
                <option value="Coordinator">{t('userManagement.roles.coordinator')}</option>
                <option value="Sales">{t('userManagement.roles.sales')}</option>
                <option value="Demo">{t('userManagement.roles.demo')}</option>
              </Select>
              {newUser.role && (
                <p className="text-sm text-gray-400 italic mt-2">
                  {t(`userManagement.roleDescriptions.${getRoleKey(newUser.role)}`)}
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setNewUser({ username: '', email: '', password: '', role: 'Producer' });
                  setGeneratedPassword('');
                  setPasswordMode('manual');
                }}
              >
                {t('common.reset')}
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? t('common.creating') : t('userManagement.createUser')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Manage Users Tab */}
      {activeSubTab === 'manage' && (
        <div className="space-y-4">
          <div className="bg-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <ICONS.Users className="w-5 h-5 mr-2" />
              {t('system.users.existingUsers')} ({users.length})
            </h3>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
                <p className="text-gray-400 mt-2">{t('common.loading')}</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8">
                <ICONS.Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">{t('system.users.noUsers')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div key={user.id} className="bg-gray-600 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">
                            {user.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h4 className="text-white font-medium">{user.username}</h4>
                          <p className="text-gray-300 text-sm">{user.email}</p>
                          <p className="text-indigo-400 text-xs">{getRoleDisplayName(user.role)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEditUser(user)}
                      >
                        <ICONS.Edit className="w-4 h-4 mr-1" />
                        {t('common.edit')}
                      </Button>
                      {/* Producer cannot delete Admin or Super Admin */}
                      {!(currentRole === 'Producer' && (user.role === 'Admin' || user.role === 'Super Admin')) && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id, user.username)}
                        >
                          <ICONS.Trash className="w-4 h-4 mr-1" />
                          {t('common.delete')}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Edit User Modal */}
          {editingUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <ICONS.Edit className="w-5 h-5 mr-2" />
                  {t('system.users.editUser')}
                </h3>
                
                <form onSubmit={handleUpdateUser} className="space-y-4">
                  <Input
                    label={t('userManagement.username')}
                    id="edit-username"
                    name="username"
                    value={editForm.username}
                    onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                    required
                  />
                  <Input
                    label={t('userManagement.email')}
                    id="edit-email"
                    name="email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    required
                  />
                  <Select
                    label={t('userManagement.role')}
                    id="edit-role"
                    name="role"
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value as User['role'] })}
                    required
                  >
                    <option value="Super Admin">{t('userManagement.roles.superAdmin')}</option>
                    <option value="Admin">{t('userManagement.roles.admin')}</option>
                    <option value="Producer">{t('userManagement.roles.producer')}</option>
                    <option value="Project Manager">{t('userManagement.roles.projectManager')}</option>
                    <option value="Coordinator">{t('userManagement.roles.coordinator')}</option>
                    <option value="Sales">{t('userManagement.roles.sales')}</option>
                    <option value="Demo">{t('userManagement.roles.demo')}</option>
                  </Select>
                  {editForm.role && (
                    <p className="text-sm text-gray-400 italic mt-2">
                      {t(`userManagement.roleDescriptions.${getRoleKey(editForm.role)}`)}
                    </p>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setEditingUser(null)}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" variant="primary" disabled={loading}>
                      {loading ? t('common.updating') : t('common.save')}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Delete User Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteUserModal.isOpen}
        onClose={() => setDeleteUserModal({ isOpen: false, userId: '', username: '' })}
        onConfirm={confirmDeleteUser}
        title={t('userManagement.deleteUserTitle') || 'Удалить пользователя'}
        message={`${t('userManagement.deleteUserConfirm') || 'Вы уверены, что хотите удалить пользователя'} "${deleteUserModal.username}"?`}
        confirmText={t('common.delete') || 'Удалить'}
        cancelText={t('common.cancel') || 'Отмена'}
        type="danger"
      />
    </div>
  );
};

export default UserManagementTab;
